import React from 'react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { Unsubscribe } from 'router5/dist/types/base';
import { IRouter } from '../../../infrastructure/router';
import { RouteParams, Routes } from '../../../routes-table';
import { PagingViewModel } from '../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { ITrackedZoomUserDTO, IZoomDAO, IZoomUser, TTrackedUsersListRequestSortKeys } from '../../../api/zoom.dao';
import { alertServerError, handleApiException, showNotification } from '../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { IAlertsService } from '../../../infrastructure/alerts';
import { IJobDescriptionDTO, IJobsDAO, IMeetingsJobDetails, JobStatus, JobType } from '../../../api/jobs.dao';
import { IJobsMonitor } from '../../../domain/jobs-monitor/jobs-monitor';
import { SortOrder } from '../../../api/shared.dto';
import { MeetingsStatus } from './meeting-status.enum';
import './tracked-users.scss';
import { FormattedMessage, IntlShape } from 'react-intl';
import { JobTrackingService } from '../../../domain/jobs/implementations/job-tracking.service';

interface ITrackedZoomUser extends ITrackedZoomUserDTO {
    // for future functionality
    meetingsStatus?: MeetingsStatus;
}

interface IJobItem {
    startedAlertKey: string;
    completedAlertKey: string;
    loading: boolean;
    alreadyJobsCompletion: number[];
    alreadyJobsFail: number[];
}

type Jobs = Partial<Record<JobType, IJobItem>>;

class TrackedUsersViewModel {
    @mobx.observable
    private trackedUsers: ITrackedZoomUser[];
    @mobx.observable
    private pagingVM: PagingViewModel;
    @mobx.observable
    private refreshAllZoomUsersLoading: boolean;
    @mobx.observable
    private refreshHostKeysLoading: boolean;
    @mobx.observable
    private searchQuery: string;

    private subscriptions: Unsubscribe[];

    private sortOrder: SortOrder;
    private sortField: TTrackedUsersListRequestSortKeys;
    @mobx.observable
    private currentUserZoomId: string;
    private trackedUsersJobs: Jobs;
    constructor(
        @IoC.Inject private readonly zoomDao: IZoomDAO,
        @IoC.Inject private readonly router: IRouter<RouteParams>,
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly jobsMonitor: IJobsMonitor = IoC.Container.get(IJobsMonitor),
        @IoC.Inject private readonly jobsDao: IJobsDAO = IoC.Container.get(IJobsDAO),
        private readonly jobTrackingService: JobTrackingService = IoC.Container.get(JobTrackingService)
    ) {
        this.pagingVM = new PagingViewModel();
        this.sortOrder = SortOrder.Asc;
        this.sortField = 'email';
        this.searchQuery = '';

        const keys = [JobType.ResynchronizeZoomUsers, JobType.RefreshZoomAccountHostKeys, JobType.UpdateZoomUserHostKey];
        this.trackedUsersJobs = {
            ...Object.fromEntries(
                keys.map(key => [key, {
                    startedAlertKey: '',
                    completedAlertKey: '',
                    loading: null,
                    alreadyJobsCompletion: [],
                    alreadyJobsFail: []
                }])
              ),
        };
        mobx.reaction(() => this.pagingVM.getCurrentPage(), () => this.fetchTrackedUsers());
    }

    public getTrackedUsers(): ITrackedZoomUser[] {
        return this.trackedUsers;
    }

    public getPagingVM() {
        return this.pagingVM;
    }

    public getSortOrder = () => {
        return this.sortOrder;
    };

    public getSortField = () => {
        return this.sortField;
    };

    public getSearchQuery = () => {
        return this.searchQuery;
    };

    public getRefreshAllZoomUsersLoading() {
        return this.refreshAllZoomUsersLoading;
    }

    public getRefreshHostKeysLoading() {
        return this.refreshHostKeysLoading;
    }

    @mobx.action
    public setSearchQuery = (query: string, needExecuteSearch = false) => {
        const newSearchQuery = query.trim();

        if (this.searchQuery !== newSearchQuery) {
            this.searchQuery = newSearchQuery;

            if (needExecuteSearch) this.executeSearch();
        }
    };

    public resetSearchAndFilter = () => {
        this.sortField = 'email';
        this.sortOrder = SortOrder.Asc;
        this.searchQuery = '';
    };

    public setupRouterListeners() {
        this.subscriptions = [];

        this.subscriptions.push(this.router.listenOthers(
            Routes.TrackedZoomUsers,
            () => {
                this.pagingVM.selectPage(1);
            },
        ));
    }

    @mobx.action
    public cleanup() {
        this.resetSearchAndFilter();
        this.trackedUsers = null;
        if (this.subscriptions) {
            this.subscriptions.forEach(x => x());
        }
    }

    @mobx.action
    public async initialize() {
        this.jobsMonitor.registerHandler(JobType.ResynchronizeZoomUsers, this.handleRefreshZoomUsers);
        this.jobsMonitor.registerHandler(JobType.RefreshZoomAccountHostKeys, this.handleRefreshHostKeys);
        this.jobsMonitor.registerHandler(JobType.UpdateZoomUserHostKey, this.handleUpdateZoomUserHostKey);
    }

    @mobx.action
    public handleRefreshZoomUsers = async (job: IJobDescriptionDTO<IMeetingsJobDetails>) => {
       this.handleJobStatusChange(JobType.ResynchronizeZoomUsers, 'Zoom Meetings refresh is complete', 'Zoom Meetings refresh failed', job);
    };

    @mobx.action
    public handleRefreshHostKeys = async (job: IJobDescriptionDTO<IMeetingsJobDetails>) => {
       this.handleJobStatusChange(JobType.RefreshZoomAccountHostKeys, 'Refresh Host Keys is complete', 'Refresh Host Keys failed', job);
    };

    @mobx.action
    public executeSearch = () => {
        const currentPage = this.pagingVM.getCurrentPage();
        if (currentPage === 1) {
            this.fetchTrackedUsers();
        } else {
            this.pagingVM.selectPage(1);
        }
    };

    @mobx.action
    public sortTableRows = async (sortField: TTrackedUsersListRequestSortKeys, sortOrder: SortOrder) => {
        this.sortField = sortField;
        this.sortOrder = sortOrder;
        await this.fetchTrackedUsers();
    };

    @mobx.action
    public fetchTrackedUsers = async () => {
        const trackedUsersResponse = await this.zoomDao.getTrackedUsers({
            size: this.pagingVM.getPageSize(),
            page: this.pagingVM.getCurrentPageForBE(),
            query: this.searchQuery || '',
            sortField: this.sortField,
            sortOrder: this.sortOrder,
        });
        mobx.runInAction(() => {
            this.trackedUsers = trackedUsersResponse.content;
            this.pagingVM.setTotalRecords(trackedUsersResponse.totalElements);
        });
    };

    @mobx.action
    public updateTrackedUsers(addUsers: Readonly<IZoomUser[]>, removeUsers: Readonly<string[]>) {
        for (const addedUser of addUsers) {
            this.trackUser(addedUser);
        }

        for (const removeUser of removeUsers) {
            this.removeTracking(removeUser);
        }
    }

    private trackUser(zoomUser: IZoomUser) {
        this.trackedUsers.push({
            firstName: zoomUser.first_name,
            zoomId: zoomUser.id,
            lastName: zoomUser.last_name,
            email: zoomUser.email,
            groups: zoomUser.groups
        });
    }

    private removeTracking(userId: string) {
        const itemToRemoveIndex = this.trackedUsers.findIndex(x => x.zoomId === userId);
        if (itemToRemoveIndex !== -1) {
            this.trackedUsers.splice(itemToRemoveIndex, 1);
        }
    }

    @mobx.action
    public async refreshAllZoomUsers() {
        try {
            this.trackedUsersJobs[JobType.ResynchronizeZoomUsers].startedAlertKey = this.alertsService.alert({
                message: 'Zoom Meetings refresh is in progress',
                okButtonProps: {
                    text: 'OK',
                },
            });
            this.trackedUsersJobs[JobType.ResynchronizeZoomUsers].loading = true;
            await this.zoomDao.refreshAllZoomMeetings();
        } catch (error) {
           this.onLongRunningJobFail(JobType.ResynchronizeZoomUsers, null, error, null);
        }
    }

    @mobx.action
    public refreshUserMeetingsSchedule = async (intl: IntlShape, userZoomId: string): Promise<void> => {
        try {
            await this.zoomDao.refreshUserMeetingsSchedule(userZoomId);
            showNotification(
                intl.formatMessage({
                    id: 'oe.zoom.zoomMeetingRefreshHasCompleted',
                    description: 'Notification about Zoom Meeting refresh completed',
                    defaultMessage: 'Zoom Meeting refresh has completed'
                })
            );
        } catch (error) {
            handleApiException(error);
        }
    };

    @mobx.action
    public refreshWebinars = async (intl: IntlShape, zoomUserId: string): Promise<void> => {
        try {
            await this.zoomDao.refreshWebinars(zoomUserId);
            showNotification(
                intl.formatMessage({
                    id: 'oe.zoom.zoomWebinarRefreshHasCompleted',
                    description: 'Notification about Zoom Webinar completed',
                    defaultMessage: 'Zoom Webinar refresh has completed'
                })
            );
        } catch (error) {
            handleApiException(error);
        }
    };

    @mobx.action
    public handleUpdateZoomUserHostKey = async (job: IJobDescriptionDTO<IMeetingsJobDetails>) => {
       this.handleJobStatusChange(JobType.UpdateZoomUserHostKey, null, 'Update Zoom User Host Key is failed', job);
    };

    @mobx.action
    public getTrackedZoomUser = async (): Promise<ITrackedZoomUserDTO> | null => {
        if (this.currentUserZoomId) {
            return await this.zoomDao.getTrackedZoomUser({ userZoomId: this.currentUserZoomId });
        } else return null;
    };


    @mobx.action
    public async updateZoomUserHostKey(userZoomId: string) {
        try {
            showNotification('Host Key refresh in progress');
            this.currentUserZoomId = userZoomId;
            await this.zoomDao.updateZoomUserHostKey({ userZoomId: userZoomId });
        } catch (error) {
          this.onLongRunningJobFail(JobType.UpdateZoomUserHostKey,null, error);
        }
    }

    @mobx.action
    public onUpdateZoomUserHostKeySuccess(jobType: JobType, jobId: number, trackedUsersResponse: ITrackedZoomUserDTO) {
        this.alertsService.closeAlert(this.trackedUsersJobs[jobType].startedAlertKey);
        this.trackedUsersJobs[jobType].startedAlertKey = null;
        const title = <FormattedMessage
            id='oe.zoom.hotKeyRefresh'
            defaultMessage='Hot Key Refresh'
            description='Hot Key Refresh title popup'
        />;
        const message = <div className='tracked-users__popup-content'>
            <div>
                <FormattedMessage
                    id='oe.zoom.hostKeyChanged'
                    defaultMessage='The Host Key for account {email} has been changed to {hostKey}.'
                    description='Host Key for account popup text'
                    values={{
                        email: trackedUsersResponse?.email,
                        hostKey: trackedUsersResponse?.hostKey
                    }}
                />
            </div>
            <br />
            <div>
                <FormattedMessage
                    id='oe.zoom.sipAddressesForUpcomingWebinars'
                    defaultMessage='Please note; any SIP addresses for upcoming webinars must now incorporate the new Host Key.
                    Any existing Webinar Connections in OE Central will need to be manually updated to reflect the new SIP address.'
                    description='Host Key for account popup description'
                />
            </div>
        </div>;
        if (trackedUsersResponse?.hostKey) {
            this.onLongRunningJobSuccess(JobType.UpdateZoomUserHostKey, title, message, jobId);
        }
    }

    @mobx.action
    public async refreshHostKeys() {
        try {
            this.trackedUsersJobs[JobType.RefreshZoomAccountHostKeys].startedAlertKey = this.alertsService.alert({
                message: 'Refresh Host Keys is in progress',
                okButtonProps: {
                    text: 'OK',
                },
            });
            this.trackedUsersJobs[JobType.RefreshZoomAccountHostKeys].loading = true;
            await this.zoomDao.refreshHostKeys();
        } catch (error) {
           this.onLongRunningJobFail(JobType.RefreshZoomAccountHostKeys, null, error, null);
        }
    }


    @mobx.action
    private async handleJobStatusChange(jobType: JobType, successMessage: string, failedMessage: string, job: IJobDescriptionDTO<IMeetingsJobDetails>) {
        switch (job.jobStatus) {
            case JobStatus.Completed: {
                if (this.trackedUsersJobs[jobType].alreadyJobsCompletion.indexOf(job.jobId) === -1) {
                    this.trackedUsersJobs[jobType].loading = false;
                    this.trackedUsersJobs[jobType].alreadyJobsCompletion.push(job.jobId);
                    if (jobType === JobType.UpdateZoomUserHostKey) {
                        const trackedUsersResponse = await this.getTrackedZoomUser();
                        this.onUpdateZoomUserHostKeySuccess(JobType.UpdateZoomUserHostKey, job.jobId, trackedUsersResponse);
                        this.currentUserZoomId = '';
                    } else this.onLongRunningJobSuccess(jobType, null, successMessage, job.jobId);
                    await this.fetchTrackedUsers();
                }
                break;
            }
            case JobStatus.Failed: {
                if (this.trackedUsersJobs[jobType].alreadyJobsFail.indexOf(job.jobId) === -1) {
                    this.trackedUsersJobs[jobType].alreadyJobsFail.push(job.jobId);
                    this.trackedUsersJobs[jobType].loading = false;
                    this.onLongRunningJobFail(jobType, failedMessage, null, job.jobId);
                }
                break;
            }

        }
    }

    @mobx.action
    public onLongRunningJobSuccess(jobType: JobType, title: string | React.ReactNode, message: string | React.ReactNode, jobId: number) {
        this.alertsService.closeAlert(this.trackedUsersJobs[jobType].startedAlertKey);
        this.trackedUsersJobs[jobType].startedAlertKey = null;
        this.trackedUsersJobs[jobType].completedAlertKey = this.alertsService.alert({
            title: title,
            message: message,
            okButtonProps: {
                text: 'OK',
                handler: async () => {
                    await this.jobsDao.markJobAsReviewed(jobId);
                }
            }
        });
    }

    @mobx.action
    public onLongRunningJobFail(jobType: JobType, message: string, error?, jobId?) {
        this.alertsService.closeAlert(this.trackedUsersJobs[jobType].startedAlertKey);
        if (error) {
            alertServerError('Error', this.errorParsingService.extractMessage(error));
        }
        else {
            this[jobType].completedAlertKey = this.alertsService.alert({
                message: message,
                okButtonProps: {
                    text: 'OK',
                    handler: async () => {
                        await this.jobsDao.markJobAsReviewed(jobId);
                    }
                }
            });
        }
        this[jobType].startedAlertKey = null;
        this[jobType].loading = false;
    }

}

export { TrackedUsersViewModel, ITrackedZoomUser };